import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from '@angular/core';

import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import {
  IssueRestrictedFields,
  SchoolRosterStudent,
  SchoolTermGradeDto,
  SchoolGradeYearDto,
  AttendanceType,
  CrossTermEntry
} from '@k12/models';
import { costsValidator } from '@k12/validators';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-generic-student-card',
  templateUrl: './generic-student-card.component.html',
  styleUrl: './generic-student-card.component.scss',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatOptionModule
  ]
})
export class GenericStudentCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() editable = false;
  @Input() student: SchoolRosterStudent | null = null;
  @Input() schoolTermGrades: SchoolTermGradeDto[] = [];
  @Input() schoolGradeYears: SchoolGradeYearDto[] = [];
  @Input() attendanceTypes: AttendanceType[] = [];
  @Input() isExpanded = false;
  @Input() issueRestrictions: IssueRestrictedFields | null = null;
  /**
   * When false, prior-term tuition cells are never locked regardless of sort order.
   * Set to false on the Not Yet Certified roster where the school enters all term
   * costs fresh; leave at the default true for all other rosters.
   */
  @Input() lockPriorTermCosts = true;
  @Input() onActionCompleted?: (studentEnrollmentId: string) => void;
  @Input() onCertifiedCallback?: (studentEnrollmentId: string) => void;
  @Input() onEndorsedCallback?: (studentEnrollmentId: string) => void;
  @Output() toggleExpand = new EventEmitter<string>();
  @Output() withdrawalRequested = new EventEmitter<SchoolRosterStudent>();

  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly PRE_K_VALUE = '__PRE_K__';

  editingStudent: SchoolRosterStudent | null = null;
  form: FormGroup;
  private valueChangesSub?: Subscription;

  // Certification mode state
  isNotAttending = false;
  preKSelected = false;
  individualCostErrors: string[] = [];
  formReady = false;

  // Snapshot of cost values taken when the user selects "Not Attending", so re-selecting an
  // attending type restores exactly what was there (Bug 4 — values must not vanish permanently).
  private notAttendingCostSnapshot: {
    standard?: number;
    individual?: number;
    termPayments: Record<string, number | undefined>;
  } | null = null;

  // Pristine deep copy of the student as loaded, captured before any in-place edits. Cancel
  // (resetForm) restores from this so term-based tuition/fees revert correctly (Bug 5).
  private pristineStudent: SchoolRosterStudent | null = null;

  chevronUpPath = 'assets/images/chevron-up.svg';
  chevronDownPath = 'assets/images/chevron-down.svg';

  constructor() {
    this.form = this.fb.group(
      {
        schoolTermGradeId: ['', Validators.required],
        attendanceTypeId: [null, Validators.required],
        academicYearStandardCostTotal: [''],
        academicYearIndividualCostTotal: ['']
      },
      { validators: costsValidator() }
    );
  }

  // ========================
  // Term Helpers
  // ========================

  get gradeInDropdown(): boolean {
    const id = this.editingStudent?.schoolTermGradeId || this.student?.schoolTermGradeId;
    return !!id && this.schoolTermGrades.some((g) => g.schoolTermGradeId === id);
  }

  get sortedTerms(): CrossTermEntry[] {
    const sorted = (this.editingStudent?.crossTermSummary ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);

    // One cost field per term. This is a permanent fix to the issue where somehow these get
    // duplicated in the CrossTermSummary array. The duplication is not expected, but this ensures that
    // we can stop dealing with this.
    const byTerm = new Map<string, CrossTermEntry>();
    for (const term of sorted) {
      const kept = byTerm.get(term.schoolTermId);
      if (!kept || term.schoolTermGradeId === this.editingStudent?.schoolTermGradeId) {
        byTerm.set(term.schoolTermId, term);
      }
    }
    return [...byTerm.values()];
  }

  isTermCostDisabled(term: CrossTermEntry): boolean {
    if (term.studentEnrollmentId) {
      // On Not Yet Certified, all enrolled term costs are editable — the school is
      // entering everything fresh and should not be blocked by sort-order locking.
      if (!this.lockPriorTermCosts) return false;
      // Lock tuition for all prior terms — if you're on term N, terms < N are never editable
      if (term.sortOrder < (this.student?.termSortOrder ?? 0)) {
        return true;
      }
      return false;
    }
    return new Date(term.endDate) < new Date();
  }

  private syncTermCostControls(): void {
    const terms = this.sortedTerms;
    // Remove old termCost_ controls
    Object.keys(this.form.controls)
      .filter((key) => key.startsWith('termCost_'))
      .forEach((key) => this.form.removeControl(key));

    // Add a control for each term
    for (const term of terms) {
      const controlName = 'termCost_' + term.schoolTermId;
      const control = this.fb.control(term.payment ?? '');
      if (this.isTermCostDisabled(term)) {
        control.disable();
      }
      this.form.addControl(controlName, control);
    }
  }

  // Bug 1: when an attending student's grade changes, the per-term tuition/fees owed fields
  // (termCost_*) must be cleared so the school re-enters costs appropriate to the new grade.
  // Standard/individual annual costs are handled separately by the existing auto-populate logic.
  private clearTermCostFields(): void {
    for (const term of this.sortedTerms) {
      const ctrl = this.form.get('termCost_' + term.schoolTermId);
      if (ctrl && !ctrl.disabled) {
        ctrl.setValue('', { emitEvent: false });
      }
      const entry = this.editingStudent?.crossTermSummary?.find((t) => t.schoolTermId === term.schoolTermId);
      if (entry) entry.payment = undefined;
    }
  }

  // ========================
  // Common Display Methods
  // ========================

  getFormattedHomePhone(): string {
    if (!this.student?.parentHomePhone) return '—';
    return 'Home: ' + this.student.parentHomePhone;
  }

  getFormattedProgramsAccepted(): string {
    if (!this.student?.studentAwards) return '—';
    return this.student.studentAwards.map((award) => award.programName).join(', ') || '—';
  }

  getFormattedAwardTier(): string {
    if (!this.student?.awardTier) return '—';
    return 'Tier ' + this.student.awardTier;
  }

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // ========================
  // Certification Mode Methods (Form Handling)
  // ========================

  syncFormToModel(): void {
    if (!this.student || !this.editingStudent) return;
    const mapValue = (controlName: string) => {
      const ctrl = this.form.get(controlName);
      if (!ctrl) return undefined;
      if (ctrl.disabled) return undefined;
      const v = ctrl.value;
      return v === '' || v === undefined ? null : v;
    };
    const standard = mapValue('academicYearStandardCostTotal');
    const individual = mapValue('academicYearIndividualCostTotal');
    if (standard !== undefined) {
      this.editingStudent.academicYearStandardCostTotal = this.coerceToCurrencyAmount(standard);
    }
    if (individual !== undefined) {
      this.editingStudent.academicYearIndividualCostTotal = this.coerceToCurrencyAmount(individual);
    }

    // Sync term payments back to crossTermSummary
    for (const term of this.editingStudent.crossTermSummary ?? []) {
      const controlName = 'termCost_' + term.schoolTermId;
      const val = mapValue(controlName);
      if (val !== undefined) {
        term.payment = this.coerceToCurrencyAmount(val);
      }
    }

    const attendance = mapValue('attendanceTypeId');
    const grade = mapValue('schoolTermGradeId');
    this.editingStudent.attendanceTypeId = attendance === undefined ? this.editingStudent.attendanceTypeId : attendance;
    // Never write the Pre-K sentinel back to the model — keep whatever grade was loaded.
    if (grade !== this.PRE_K_VALUE) {
      this.editingStudent.schoolTermGradeId = grade === undefined ? this.editingStudent.schoolTermGradeId : grade;
    }

    // editingStudent is a shallow copy of student, so crossTermSummary entries are shared references.
    // Copying scalar fields back explicitly ensures child components that hold the student reference see updates.
    this.student.academicYearStandardCostTotal = this.editingStudent.academicYearStandardCostTotal;
    this.student.academicYearIndividualCostTotal = this.editingStudent.academicYearIndividualCostTotal;
    this.student.attendanceTypeId = this.editingStudent.attendanceTypeId;
    this.student.schoolTermGradeId = this.editingStudent.schoolTermGradeId;
  }

  validateIndividualCost(): void {
    this.individualCostErrors = [];
    const errors = this.form.errors || {};
    if (errors['costsRequired']) {
      const termNames = this.sortedTerms.map((t) => t.termName.toLowerCase());
      const termNamesFormatted = this.formatTermNamesList(termNames);
      this.individualCostErrors.push(`All cost fields (standard, individual, ${termNamesFormatted}) are required.`);
    }
    if (errors['individualCostTooHigh'])
      this.individualCostErrors.push('Individual cost must be less than standard cost.');
    if (errors['individualCostNotSum'])
      this.individualCostErrors.push('Individual cost must equal the sum of all term costs.');
  }

  private formatTermNamesList(names: string[]): string {
    if (names.length === 0) return 'term costs';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]}, and ${names[1]}`;
    const allButLast = names.slice(0, -1).join(', ');
    return `${allButLast}, and ${names.at(-1)}`;
  }

  protected onGradeChange(): void {
    if (!this.student) return;
    const selectedValue = this.form.get('schoolTermGradeId')?.value;
    if (!selectedValue) return;

    if (selectedValue === this.PRE_K_VALUE) {
      this.preKSelected = true;
      // Restore the original loaded grade on the model — Pre-K is front-end only.
      // editingStudent.schoolTermGradeId intentionally left as-is (whatever was loaded).
      const notAttendingType = this.attendanceTypes.find((t) => t.name?.toLowerCase().includes('not'));
      if (notAttendingType) {
        this.form.patchValue({ attendanceTypeId: notAttendingType.attendanceTypeId }, { emitEvent: false });
        if (this.editingStudent) this.editingStudent.attendanceTypeId = Number(notAttendingType.attendanceTypeId);
        this.handleNotAttending();
      }
      this.individualCostErrors = [];
      this.form.updateValueAndValidity({ emitEvent: false });
      return;
    }

    this.preKSelected = false;
    const previousGradeId = this.editingStudent?.schoolTermGradeId;
    const gradeActuallyChanged = !!previousGradeId && previousGradeId !== selectedValue;
    if (this.editingStudent) this.editingStudent.schoolTermGradeId = selectedValue;
    if (!this.isNotAttending) {
      // Clear term-based tuition/fees first so the new grade starts blank for re-entry (Bug 1).
      if (gradeActuallyChanged) this.clearTermCostFields();
      this.handleAttendingGradeChange(selectedValue);
    }
    this.individualCostErrors = [];
    this.form.updateValueAndValidity({ emitEvent: false });
    this.validateIndividualCost();
  }

  private handleAttendingGradeChange(selectedSchoolTermGradeId: string): void {
    this.updateCrossTermGradeIds(selectedSchoolTermGradeId);

    // Check if standard cost was originally set (from the loaded student data, not auto-calculated)
    const originalStandardCost = this.student?.academicYearStandardCostTotal;
    const hadOriginalCost = originalStandardCost != null && originalStandardCost !== 0;

    // Only update costs if the original student data didn't have a cost value
    if (!hadOriginalCost) {
      const standardCosts = this.getStandardCost(selectedSchoolTermGradeId);
      if (standardCosts > 0) {
        this.setStandardAndIndividualCosts(standardCosts);
        this.patchFormAndTermsForStandardCost(standardCosts);
      } else {
        this.seedFormFromStudent();
      }
    }
  }

  private updateCrossTermGradeIds(selectedSchoolTermGradeId: string): void {
    if (!this.editingStudent?.crossTermSummary) return;
    const newGrade = this.schoolTermGrades.find((g) => g.schoolTermGradeId === selectedSchoolTermGradeId);
    if (!newGrade?.schoolGradeId) return;
    const now = new Date();
    for (const term of this.editingStudent.crossTermSummary) {
      if (new Date(term.endDate) >= now) {
        const match = this.schoolTermGrades.find(
          (g) => g.schoolTermId === term.schoolTermId && g.schoolGradeId === newGrade.schoolGradeId
        );
        if (match) {
          term.schoolTermGradeId = match.schoolTermGradeId;
        }
      }
    }
  }

  private setStandardAndIndividualCosts(standardCosts: number): void {
    if (this.editingStudent) {
      this.editingStudent.academicYearStandardCostTotal = standardCosts;
      this.editingStudent.academicYearIndividualCostTotal = standardCosts;
    }
  }

  private patchFormAndTermsForStandardCost(standardCosts: number): void {
    const patchValue: Record<string, unknown> = {
      academicYearStandardCostTotal: standardCosts,
      academicYearIndividualCostTotal: standardCosts
    };
    // Preserve existing term payment values from the student object instead of dividing
    for (const term of this.sortedTerms) {
      const controlName = 'termCost_' + term.schoolTermId;
      // Use existing payment value if available, otherwise leave empty
      patchValue[controlName] = term.payment ?? '';
    }
    this.form.patchValue(patchValue, { emitEvent: false });
  }

  onAttendanceTypeChange(): void {
    if (!this.student) return;
    const selectedStatusId = this.form.get('attendanceTypeId')?.value;
    if (!selectedStatusId) return;

    if (this.editingStudent) {
      this.editingStudent.attendanceTypeId = selectedStatusId;
    }

    const status = this.attendanceTypes?.find((s) => s?.attendanceTypeId === selectedStatusId);
    const label = status?.name || '';
    const includesNot = typeof label === 'string' && label.toLowerCase().includes('not');

    if (includesNot) {
      this.handleNotAttending();
    } else {
      this.handleAttending();
    }
  }

  onWithdrawal(student: SchoolRosterStudent) {
    this.withdrawalRequested.emit(student);
  }

  private handleNotAttending(): void {
    this.isNotAttending = true;
    // Snapshot current cost values before we wipe them so handleAttending() can restore them.
    this.notAttendingCostSnapshot = {
      standard: this.editingStudent?.academicYearStandardCostTotal ?? undefined,
      individual: this.editingStudent?.academicYearIndividualCostTotal ?? undefined,
      termPayments: Object.fromEntries(
        (this.editingStudent?.crossTermSummary ?? []).map((t) => [t.schoolTermId, t.payment])
      )
    };
    const costControls = ['academicYearStandardCostTotal', 'academicYearIndividualCostTotal'];
    const termControls = this.sortedTerms.map((t) => 'termCost_' + t.schoolTermId);
    [...costControls, ...termControls].forEach((c) => {
      const ctrl = this.form.get(c);
      if (ctrl) {
        ctrl.setValue(null, { emitEvent: false });
        ctrl.disable({ emitEvent: false });
      }
    });
    if (this.editingStudent) {
      this.editingStudent.academicYearStandardCostTotal = undefined;
      this.editingStudent.academicYearIndividualCostTotal = undefined;
      for (const term of this.editingStudent.crossTermSummary ?? []) {
        term.payment = undefined;
      }
    }
    this.individualCostErrors = [];
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private handleAttending(): void {
    this.isNotAttending = false;
    // Restore the values captured when the user went "Not Attending" (Bug 4).
    if (this.notAttendingCostSnapshot && this.editingStudent) {
      this.editingStudent.academicYearStandardCostTotal = this.notAttendingCostSnapshot.standard;
      this.editingStudent.academicYearIndividualCostTotal = this.notAttendingCostSnapshot.individual;
      for (const term of this.editingStudent.crossTermSummary ?? []) {
        if (term.schoolTermId in this.notAttendingCostSnapshot.termPayments) {
          term.payment = this.notAttendingCostSnapshot.termPayments[term.schoolTermId];
        }
      }
      this.notAttendingCostSnapshot = null;
    }
    const costControls = ['academicYearStandardCostTotal', 'academicYearIndividualCostTotal'];
    costControls.forEach((c) => {
      const ctrl = this.form.get(c);
      if (!ctrl) return;
      // Keep standard cost locked when prior-term locking is active — "Not Attending"
      // disabled it for a different reason and we must restore the locked state.
      if (c === 'academicYearStandardCostTotal' && this.lockPriorTermCosts) return;
      ctrl.enable({ emitEvent: false });
    });
    // Only re-enable term controls that aren't permanently disabled (past terms with no enrollment)
    for (const term of this.sortedTerms) {
      if (!this.isTermCostDisabled(term)) {
        const ctrl = this.form.get('termCost_' + term.schoolTermId);
        if (ctrl) ctrl.enable({ emitEvent: false });
      }
    }
    // Don't update costs when attendance type changes - just restore existing values
    if (this.editingStudent) {
      const patchValue: Record<string, unknown> = {
        academicYearStandardCostTotal: this.editingStudent.academicYearStandardCostTotal ?? '',
        academicYearIndividualCostTotal: this.editingStudent.academicYearIndividualCostTotal ?? ''
      };
      // Restore existing term payment values
      for (const term of this.sortedTerms) {
        const controlName = 'termCost_' + term.schoolTermId;
        patchValue[controlName] = term.payment ?? '';
      }
      this.form.patchValue(patchValue, { emitEvent: false });
    }
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private setStandardCostsForGrade(schoolTermGradeId: string): void {
    const standardCosts = this.getStandardCost(schoolTermGradeId);
    if (standardCosts > 0) {
      if (this.editingStudent) {
        this.editingStudent.academicYearStandardCostTotal = standardCosts;
        this.editingStudent.academicYearIndividualCostTotal = standardCosts;
      }
      const patchValue: Record<string, unknown> = {
        academicYearStandardCostTotal: standardCosts,
        academicYearIndividualCostTotal: standardCosts
      };
      // Preserve existing term payment values from the student object instead of dividing
      for (const term of this.sortedTerms) {
        const controlName = 'termCost_' + term.schoolTermId;
        // Use existing payment value if available, otherwise leave empty
        const entry = this.editingStudent?.crossTermSummary?.find((t) => t.schoolTermId === term.schoolTermId);
        patchValue[controlName] = entry?.payment ?? '';
      }
      this.form.patchValue(patchValue, { emitEvent: false });
    }
  }

  onStandardCostChange(): void {
    if (this.editingStudent) {
      this.editingStudent.academicYearStandardCostTotal = this.coerceToCurrencyAmount(
        this.form.get('academicYearStandardCostTotal')?.value
      );
      this.form.patchValue(
        { academicYearStandardCostTotal: this.editingStudent.academicYearStandardCostTotal },
        { emitEvent: false }
      );
    }
  }

  onIndividualCostChange(): void {
    if (this.editingStudent) {
      this.editingStudent.academicYearIndividualCostTotal = this.coerceToCurrencyAmount(
        this.form.get('academicYearIndividualCostTotal')?.value
      );
      this.form.patchValue(
        { academicYearIndividualCostTotal: this.editingStudent.academicYearIndividualCostTotal },
        { emitEvent: false }
      );
    }
  }

  onMoneyInput(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replaceAll(/\D/g, '');
    if (cleaned !== input.value) input.value = cleaned;
    this.form.patchValue({ [controlName]: cleaned === '' ? '' : Number.parseInt(cleaned, 10) }, { emitEvent: false });
  }

  onMoneyBlur(controlName: string): void {
    const raw = this.form.get(controlName)?.value;
    const coerced = this.coerceToCurrencyAmount(raw);
    this.form.patchValue({ [controlName]: coerced }, { emitEvent: false });
    if (!this.student) return;
    if (controlName === 'academicYearStandardCostTotal') {
      this.student.academicYearStandardCostTotal = coerced;
    } else if (controlName === 'academicYearIndividualCostTotal') {
      this.student.academicYearIndividualCostTotal = coerced;
    } else if (controlName.startsWith('termCost_')) {
      const schoolTermId = controlName.replace('termCost_', '');
      const entry = this.student.crossTermSummary?.find((t) => t.schoolTermId === schoolTermId);
      if (entry) entry.payment = coerced;
    }
  }

  private coerceToCurrencyAmount(value: string | number | null | undefined): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const n = Number(value);
    if (Number.isNaN(n) || !Number.isFinite(n)) return undefined;
    return Math.max(0, Math.round((n + Number.EPSILON) * 100) / 100);
  }

  onMoneyKeydown(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'Tab',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Delete',
      'Home',
      'End'
    ];
    if (allowedKeys.includes(event.key)) return;
    if ((event.ctrlKey || event.metaKey) && ['c', 'v', 'x', 'a', 'C', 'V', 'X', 'A'].includes(event.key)) return;
    if (!/^\d$/.test(event.key)) event.preventDefault();
  }

  onMoneyPaste(event: ClipboardEvent, controlName: string): void {
    const paste = event.clipboardData?.getData('text') || '';
    const cleaned = paste.replaceAll(/\D/g, '');
    if (cleaned !== paste) {
      event.preventDefault();
      const el = event.target as HTMLInputElement;
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const newValue = el.value.substring(0, start) + cleaned + el.value.substring(end);
      el.value = newValue;
      this.form.patchValue(
        { [controlName]: newValue === '' ? '' : Number.parseInt(newValue, 10) },
        { emitEvent: false }
      );
    }
  }

  private getStandardCost(schoolTermGradeId: string): number {
    if (!schoolTermGradeId || !this.schoolTermGrades.length) return 0;

    // Find the schoolGradeId from the selected schoolTermGrade
    const matchingTermGrade = this.schoolTermGrades.find((grade) => grade.schoolTermGradeId === schoolTermGradeId);
    if (!matchingTermGrade) return 0;

    // Look up tuition from SchoolGradeYearDto (tuition is now stored per grade per academic year)
    const matchingGradeYear = this.schoolGradeYears.find(
      (gradeYear) => gradeYear.schoolGradeId === matchingTermGrade.schoolGradeId
    );

    return matchingGradeYear?.tuition || 0;
  }

  // ========================
  // Lifecycle Hooks
  // ========================

  ngOnInit(): void {
    this.editingStudent = this.student ?? null;
    this.capturePristineStudent();
    if (this.editable) {
      this.syncTermCostControls();
      this.populateForm();
      if (this.isExpanded) {
        this.formReady = false;
        this.valueChangesSub = this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
          this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
          this.validateIndividualCost();
        });
      }
    } else if (this.isExpanded) {
      // Readonly mode — no form seeding needed
      this.formReady = true;
    }
  }

  ngOnDestroy(): void {
    this.valueChangesSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only recreate editingStudent when the actual student input changes, not when expand state changes
    if (changes['student'] && changes['student'].currentValue !== changes['student'].previousValue) {
      this.editingStudent = this.student ?? null;
      this.capturePristineStudent();
      if (this.editable) {
        this.syncTermCostControls();
        this.populateForm();
      }
    }

    // Handle expansion state changes separately without resetting editingStudent
    if (changes['isExpanded']) {
      if (this.editable) {
        this.handleExpandedStateChange();
        // Only populate form on initial expansion if we have data
        if (this.isExpanded && this.student && !this.editingStudent) {
          this.editingStudent = this.student;
          this.syncTermCostControls();
          this.populateForm();
        }
      } else if (this.isExpanded) {
        this.formReady = true;
      }
    }
  }

  private handleExpandedStateChange(): void {
    if (this.isExpanded && !this.valueChangesSub) {
      this.formReady = false;
      this.valueChangesSub = this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
        this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
        this.validateIndividualCost();
      });
      // Seed (or re-seed) the form so that applyPriorTermLocking fires and locks the
      // correct fields. seedFormFromStudent uses editingStudent as its source, so any
      // unsaved changes the user already made are preserved.
      this.populateForm();
    } else if (!this.isExpanded && this.valueChangesSub) {
      this.valueChangesSub.unsubscribe();
      this.valueChangesSub = undefined;
      this.formReady = false;
    }
  }

  private populateForm(): void {
    if (this.student && this.isExpanded && this.editable) {
      setTimeout(() => this.seedFormFromStudent(), 0);
    }
  }

  private seedFormFromStudent(): void {
    const source = this.editingStudent ?? this.student;
    if (!source || !this.editingStudent) return;

    this.normalizeStudentCosts(this.editingStudent);
    this.syncTermCostControls();

    // Try by schoolTermGradeId (exact match — same term and grade)
    if (source.schoolTermGradeId && this.schoolTermGrades.length) {
      const bySchoolTerm = this.schoolTermGrades.find((g) => g.schoolTermGradeId === source.schoolTermGradeId);
      if (bySchoolTerm) {
        this.patchFormForGrade(bySchoolTerm.schoolTermGradeId, source);
        this.finalizeFormReady();
        return;
      }
    }

    // Fallback: match by gradeId for cost population only.
    // Do NOT overwrite schoolTermGradeId — the matched entry may belong to a
    // different term, and writing it back would silently move the enrollment.
    if (source.gradeId && this.schoolTermGrades.length) {
      const byGrade = this.schoolTermGrades.find((g) => g.gradeId === source.gradeId);
      if (byGrade) {
        const originalSchoolTermGradeId = source.schoolTermGradeId;
        this.patchFormForGrade(byGrade.schoolTermGradeId, source);
        // Restore the original schoolTermGradeId so the update doesn't change the term
        if (originalSchoolTermGradeId) {
          this.editingStudent.schoolTermGradeId = originalSchoolTermGradeId;
          this.form.patchValue({ schoolTermGradeId: originalSchoolTermGradeId }, { emitEvent: false });
        }
        this.finalizeFormReady();
        return;
      }
    }

    // Default patch
    this.patchFormDefault(source);
    this.finalizeFormReady();
  }

  private capturePristineStudent(): void {
    if (!this.student) {
      this.pristineStudent = null;
      return;
    }
    this.pristineStudent = {
      ...this.student,
      crossTermSummary: this.student.crossTermSummary?.map((t) => ({ ...t })) ?? []
    };
  }

  resetForm(): void {
    if (!this.student) return;
    // Restore the original term-based costs onto the live student object, since in-place edits
    // (onMoneyBlur / syncFormToModel) mutated it during editing (Bug 5).
    if (this.pristineStudent) {
      this.student.academicYearStandardCostTotal = this.pristineStudent.academicYearStandardCostTotal;
      this.student.academicYearIndividualCostTotal = this.pristineStudent.academicYearIndividualCostTotal;
      this.student.attendanceTypeId = this.pristineStudent.attendanceTypeId;
      this.student.schoolTermGradeId = this.pristineStudent.schoolTermGradeId;
      // Fresh copies from the pristine snapshot; editingStudent (set below) shares this same array.
      this.student.crossTermSummary = this.pristineStudent.crossTermSummary?.map((t) => ({ ...t })) ?? [];
    }
    this.editingStudent = this.student;
    this.notAttendingCostSnapshot = null;
    this.isNotAttending = false;
    this.preKSelected = false;
    // Re-enable cost controls disabled by handleNotAttending() so seedFormFromStudent() starts
    // from a clean enabled state. applyPriorTermLocking() will re-lock as needed.
    this.form.get('academicYearStandardCostTotal')?.enable({ emitEvent: false });
    this.form.get('academicYearIndividualCostTotal')?.enable({ emitEvent: false });
    this.syncTermCostControls();
    this.seedFormFromStudent();
  }

  private finalizeFormReady(): void {
    this.individualCostErrors = [];
    this.form.updateValueAndValidity({ emitEvent: false });
    this.applyPriorTermLocking();
    this.formReady = true;
    this.cdr.markForCheck();
  }

  private applyPriorTermLocking(): void {
    // On rosters with prior-term locking active (e.g. Not Yet Endorsed), grade and
    // standard cost are fixed — only attendance type, individual cost, and current/future
    // term fees may be changed.
    if (!this.lockPriorTermCosts) return;

    this.form.get('schoolTermGradeId')?.disable({ emitEvent: false });
    this.form.get('academicYearStandardCostTotal')?.disable({ emitEvent: false });
  }

  private patchFormForGrade(schoolTermGradeId: string, source: SchoolRosterStudent): void {
    const standardCosts = this.getStandardCost(schoolTermGradeId);
    this.editingStudent!.schoolTermGradeId = schoolTermGradeId;
    const patchValue: Record<string, unknown> = {
      schoolTermGradeId,
      attendanceTypeId: source?.attendanceTypeId ?? null
    };
    if (standardCosts > 0) {
      const useStandardCost =
        source.academicYearStandardCostTotal == null || source.academicYearStandardCostTotal === 0;
      const effectiveStandard = (useStandardCost ? standardCosts : source.academicYearStandardCostTotal) ?? 0;
      const roundedStandard = this.coerceToCurrencyAmount(effectiveStandard);
      const roundedIndividual = this.coerceToCurrencyAmount(
        source.academicYearIndividualCostTotal ?? effectiveStandard
      );

      this.editingStudent!.academicYearStandardCostTotal = roundedStandard;
      this.editingStudent!.academicYearIndividualCostTotal = roundedIndividual;
      patchValue['academicYearStandardCostTotal'] = roundedStandard ?? '';
      patchValue['academicYearIndividualCostTotal'] = roundedIndividual ?? '';

      // Patch term payment controls from crossTermSummary
      for (const term of this.sortedTerms) {
        patchValue['termCost_' + term.schoolTermId] = this.coerceToCurrencyAmount(term.payment) ?? '';
      }
    } else {
      patchValue['academicYearStandardCostTotal'] =
        this.coerceToCurrencyAmount(source?.academicYearStandardCostTotal) ?? '';
      patchValue['academicYearIndividualCostTotal'] =
        this.coerceToCurrencyAmount(source?.academicYearIndividualCostTotal) ?? '';
      for (const term of this.sortedTerms) {
        patchValue['termCost_' + term.schoolTermId] = this.coerceToCurrencyAmount(term.payment) ?? '';
      }
    }
    this.form.patchValue(patchValue, { emitEvent: false });
  }

  private patchFormDefault(source: SchoolRosterStudent): void {
    const patchValue: Record<string, unknown> = {
      schoolTermGradeId: source?.schoolTermGradeId || '',
      attendanceTypeId: source?.attendanceTypeId ?? null,
      academicYearStandardCostTotal: this.coerceToCurrencyAmount(source?.academicYearStandardCostTotal) ?? '',
      academicYearIndividualCostTotal: this.coerceToCurrencyAmount(source?.academicYearIndividualCostTotal) ?? ''
    };
    for (const term of this.sortedTerms) {
      patchValue['termCost_' + term.schoolTermId] = this.coerceToCurrencyAmount(term.payment) ?? '';
    }
    this.form.patchValue(patchValue, { emitEvent: false });
  }

  private normalizeStudentCosts(student: SchoolRosterStudent): void {
    student.academicYearStandardCostTotal = this.coerceToCurrencyAmount(student.academicYearStandardCostTotal);
    student.academicYearIndividualCostTotal = this.coerceToCurrencyAmount(student.academicYearIndividualCostTotal);
    for (const term of student.crossTermSummary ?? []) {
      term.payment = this.coerceToCurrencyAmount(term.payment);
    }
  }
}
